import { isPhoneUserAgent, isPublicEngagementAccessKey } from '@kiraah-shared/common';
import { IEnvironmentInfo } from '@kiraah-shared/environment';

export const SIGNATURE_GATEWAY_QUERY_PARAM = 'signGateway';
export const LEGACY_SIGNATURE_GATEWAY_QUERY_PARAM = 'kiraahSignatureGateway';
export const LEARNING_CAMPAIGN_GATEWAY_QUERY_PARAM = 'campGateway';
export const LEGACY_LEARNING_CAMPAIGN_GATEWAY_QUERY_PARAM = 'kiraahLearningCampaignGateway';
export const PRESENCE_GATEWAY_QUERY_PARAM = 'presGateway';
export const LEGACY_PRESENCE_GATEWAY_QUERY_PARAM = 'kiraahPresenceGateway';
export const OPEN_ENGAGEMENT_GATEWAY_QUERY_PARAM = 'engGateway';

const PUBLIC_SIGNATURE_QUERY_PARAM = 'kiraahPublicSignature';
const PUBLIC_LEARNING_CAMPAIGN_QUERY_PARAM = 'kiraahPublicLearningCampaign';
const PUBLIC_PRESENCE_QUERY_PARAM = 'kiraahPublicPresence';

const PUBLIC_LEARNING_CAMPAIGN_PAGE_QUERY_PARAM = 'kiraahPublicLearningCampaignPage';
const PUBLIC_OPEN_ENGAGEMENT_QUERY_PARAM = 'kiraahPublicEngagement';

const GATEWAY_TO_MOBILE_PUBLIC = [
  {
    gatewayQueryParams: [SIGNATURE_GATEWAY_QUERY_PARAM, LEGACY_SIGNATURE_GATEWAY_QUERY_PARAM],
    publicQueryParam: PUBLIC_SIGNATURE_QUERY_PARAM,
    mobilePublicRoute: 'e-signature/public',
  },
  {
    gatewayQueryParams: [
      LEARNING_CAMPAIGN_GATEWAY_QUERY_PARAM,
      LEGACY_LEARNING_CAMPAIGN_GATEWAY_QUERY_PARAM,
    ],
    publicQueryParam: PUBLIC_LEARNING_CAMPAIGN_QUERY_PARAM,
    mobilePublicRoute: 'learning-campaign/public',
  },
  {
    gatewayQueryParams: [PRESENCE_GATEWAY_QUERY_PARAM, LEGACY_PRESENCE_GATEWAY_QUERY_PARAM],
    publicQueryParam: PUBLIC_PRESENCE_QUERY_PARAM,
    mobilePublicRoute: 'presence/public',
  },
  {
    gatewayQueryParams: [OPEN_ENGAGEMENT_GATEWAY_QUERY_PARAM],
    publicQueryParam: PUBLIC_OPEN_ENGAGEMENT_QUERY_PARAM,
    mobilePublicRoute: 'engagement/public',
  },
] as const;

function encodeGatewayToken(token: string): string {
  try {
    return encodeURIComponent(decodeURIComponent(token));
  } catch {
    return encodeURIComponent(token);
  }
}

function resolveMobilePwaBaseUrl(
  environment: Pick<IEnvironmentInfo, 'MobilePwaUrl' | 'WebAppUrl' | 'MobileAppDistribution'>
): string {
  const source =
    environment.MobilePwaUrl?.trim()
    || environment.MobileAppDistribution?.pwaUrl?.trim()
    || environment.WebAppUrl?.trim()
    || '';
  const hashIndex = source.indexOf('#');
  return (hashIndex >= 0 ? source.slice(0, hashIndex) : source).replace(/\/$/, '');
}

/** Path URL survives cross-origin redirects from e-mail clients better than hash or query. */
function buildMobilePublicPathUrl(
  mobileBase: string,
  mobilePublicRoute: string,
  encodedToken: string,
  page?: string
): string {
  const route = page
    ? `${mobilePublicRoute}/${encodedToken}/${page}`
    : `${mobilePublicRoute}/${encodedToken}`;
  return `${mobileBase}/${route}`;
}

function promoteGatewayQueryRoute(queryParam: string, hashRoutePrefix: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const token = new URLSearchParams(window.location.search).get(queryParam)?.trim();
  if (!token) {
    return;
  }

  window.history.replaceState(null, '', `/#/${hashRoutePrefix}${encodeGatewayToken(token)}`);
}

/**
 * Phone + email gateway query param → mobile PWA public URL.
 * Used before Angular bootstraps so the web shell never loads on mobile.
 */
export function resolvePhoneGatewayMobilePublicUrl(
  environment: Pick<IEnvironmentInfo, 'WebAppUrl' | 'MobilePwaUrl' | 'MobileAppDistribution'>,
  userAgent: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): string | null {
  if (!isPhoneUserAgent(userAgent)) {
    return null;
  }

  if (!environment.MobilePwaUrl?.trim() && !environment.MobileAppDistribution?.pwaUrl?.trim()) {
    return null;
  }

  const mobileBase = resolveMobilePwaBaseUrl(environment);
  if (!mobileBase) {
    return null;
  }

  const params = new URLSearchParams(search);
  for (const { gatewayQueryParams, publicQueryParam, mobilePublicRoute } of GATEWAY_TO_MOBILE_PUBLIC) {
    let token = params.get(publicQueryParam)?.trim() ?? '';
    if (!token) {
      for (const gatewayQueryParam of gatewayQueryParams) {
        token = params.get(gatewayQueryParam)?.trim() ?? '';
        if (token) {
          break;
        }
      }
    }

    if (!token) {
      continue;
    }

    const encodedToken = encodeGatewayToken(token);
    const page = mobilePublicRoute === 'learning-campaign/public'
      ? params.get(PUBLIC_LEARNING_CAMPAIGN_PAGE_QUERY_PARAM)?.trim()
      : undefined;

    return buildMobilePublicPathUrl(mobileBase, mobilePublicRoute, encodedToken, page);
  }

  return null;
}

export function tryRedirectPhoneGatewayQueryToMobilePwa(
  environment: Pick<IEnvironmentInfo, 'WebAppUrl' | 'MobilePwaUrl' | 'MobileAppDistribution'>
): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const targetUrl = resolvePhoneGatewayMobilePublicUrl(environment);
  if (!targetUrl) {
    return false;
  }

  window.location.replace(targetUrl);
  return true;
}

/**
 * Optional helper — not used at web bootstrap (same as nominal: only `?engGateway=` redirects phones to mobile).
 * `#/engagement/{publicationUid}/{slug}` stays on int web; copy link uses `buildPublicEngagementUrl` in the editor.
 */
export function resolvePhoneOpenEngagementHashMobileUrl(
  environment: Pick<IEnvironmentInfo, 'WebAppUrl' | 'MobilePwaUrl' | 'MobileAppDistribution'>,
  userAgent: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
  hash: string = typeof window !== 'undefined' ? window.location.hash : '',
  origin: string = typeof window !== 'undefined' ? window.location.origin : ''
): string | null {
  if (!isPhoneUserAgent(userAgent) || !hash.startsWith('#/engagement/')) {
    return null;
  }

  const mobileBase = resolveMobilePwaBaseUrl(environment);
  if (!mobileBase) {
    return null;
  }

  try {
    if (new URL(mobileBase).origin === origin) {
      return null;
    }
  } catch {
    return null;
  }

  const suffix = hash.slice('#/engagement/'.length).split('?')[0]?.trim() ?? '';
  if (!suffix || suffix.startsWith('public/') || suffix.startsWith('gateway/')) {
    return null;
  }

  const segments = suffix.split('/').filter(Boolean);
  const accessKey = decodeURIComponent(segments[0] ?? '').trim();
  if (segments.length < 2 || !isPublicEngagementAccessKey(accessKey)) {
    return null;
  }

  const route = `engagement/${segments.map((segment) => encodeURIComponent(decodeURIComponent(segment))).join('/')}`;
  return `${mobileBase}/#/${route}`;
}

export function tryRedirectPhoneOpenEngagementHashToMobilePwa(
  environment: Pick<IEnvironmentInfo, 'WebAppUrl' | 'MobilePwaUrl' | 'MobileAppDistribution'>
): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const targetUrl = resolvePhoneOpenEngagementHashMobileUrl(environment);
  if (!targetUrl) {
    return false;
  }

  window.location.replace(targetUrl);
  return true;
}

/** Promote `?signGateway=` (or legacy `?kiraahSignatureGateway=`) to `#/e-signature/gateway/{token}` before router bootstrap. */
export function promoteSignatureGatewayQueryRoute(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  for (const queryParam of [SIGNATURE_GATEWAY_QUERY_PARAM, LEGACY_SIGNATURE_GATEWAY_QUERY_PARAM]) {
    const token = params.get(queryParam)?.trim();
    if (token) {
      promoteGatewayQueryRoute(queryParam, 'e-signature/gateway/');
      return;
    }
  }
}

function promoteFirstMatchingGatewayQueryRoute(
  queryParams: readonly string[],
  hashRoutePrefix: string
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  for (const queryParam of queryParams) {
    const token = params.get(queryParam)?.trim();
    if (token) {
      promoteGatewayQueryRoute(queryParam, hashRoutePrefix);
      return;
    }
  }
}

/** Promote `?presGateway=` (or legacy) to `#/presence/gateway/{token}` before router bootstrap. */
export function promotePresenceGatewayQueryRoute(): void {
  promoteFirstMatchingGatewayQueryRoute(
    [PRESENCE_GATEWAY_QUERY_PARAM, LEGACY_PRESENCE_GATEWAY_QUERY_PARAM],
    'presence/gateway/'
  );
}

/** Promote `?campGateway=` (or legacy) to `#/learning-campaign/gateway/{token}` before router bootstrap. */
export function promoteLearningCampaignGatewayQueryRoute(): void {
  promoteFirstMatchingGatewayQueryRoute(
    [LEARNING_CAMPAIGN_GATEWAY_QUERY_PARAM, LEGACY_LEARNING_CAMPAIGN_GATEWAY_QUERY_PARAM],
    'learning-campaign/gateway/'
  );
}

/** Promote `?engGateway=` to `#/engagement/gateway/{token}` before router bootstrap. */
export function promoteOpenEngagementGatewayQueryRoute(): void {
  promoteFirstMatchingGatewayQueryRoute(
    [OPEN_ENGAGEMENT_GATEWAY_QUERY_PARAM],
    'engagement/gateway/'
  );
}

/** Desktop/tablet: promote gateway query params to hash routes. */
export function promotePublicGatewayQueryRoutes(): void {
  promoteSignatureGatewayQueryRoute();
  promotePresenceGatewayQueryRoute();
  promoteLearningCampaignGatewayQueryRoute();
  promoteOpenEngagementGatewayQueryRoute();
}
